import { IntlShape } from "react-intl"
import { DateTime } from "luxon"
import { Field, WritingLabelRule } from "../store/ducks/writingLabels.ducks"
import { WritingLinePayload } from "./inputMask"
import { FullDocumentOnly } from "../store/ducks/inputMask.ducks"
import { safeName, safeReference } from "./string"
import {
  LegalEntityInfo,
  LegalEntityWithoutInfos,
} from "../store/ducks/invoicing.duck"
import { resolveMerchantCodeByRate } from "./merchantCodesByRate"
import { MerchantCodeByRate } from "../store/ducks/merchantCodesByRate.ducks"

export const formatValuesRules = (rule: WritingLabelRule, intl: IntlShape) => {
  if (!rule || !rule.values || rule.values.length === 0) return ""

  return rule.values.reduce((result, val) => {
    if (val === "free_text") {
      result += rule.free_text
    } else if (val === "space") {
      result += " "
    } else {
      result += removeSpacesFromString(
        intl.formatMessage({
          id: `office-company.writing-labels.table.values.${val}`,
        })
      )
    }
    return result
  }, "")
}

export interface FreeText {
  value: "free_text"
  label: string
}

export const ArrayDocumentType = [
  "all",
  "invoice",
  "credit_note",
  "bill_of_exchange",
] as const
export type WritingLabelDocumentType = (typeof ArrayDocumentType)[number]

export const ArrayBuyOrSell = ["all", "buy", "sell"] as const
export type WritingLabelBuyOrSell = (typeof ArrayBuyOrSell)[number]

export const ArraySeparator = ["space", "dot", "underscore", "hyphen"] as const
export type Separators = (typeof ArraySeparator)[number]

export const ArrayCropType = ["left", "middle", "right"] as const
export type CropType = (typeof ArrayCropType)[number]

export const ArrayTransformType = ["lowercase", "uppercase"] as const
export type TransformType = (typeof ArrayTransformType)[number]

export const ArrayDateFormatType = ["dd_mm_yyyy", "yyyy_mm_dd"] as const
export type DateFormatType = (typeof ArrayDateFormatType)[number]

export const ArraySelectableValues = [
  "amount_tax_included",
  "amount_tax_excluded",
  "currency",
  "legal_entity_name",
  "merchant_name",
  "merchant_code",
  "date",
  "due_date",
  "periodicity",
  "document_reference",
  "automatic_reference",
  "document_reference_or_automatic_reference",
  "automatic_reference_or_document_reference",
  "original_batch_name",
  "free_text_full_doc",
  "excluding_tax_original_currency",
  "including_tax_original_currency",
] as const

export const ArraySelectableUnitsValues = [
  "unit",
  "unit_quantity",
  "unit_description",
  "unit_2",
  "unit_2_quantity",
  "unit_2_description",
]

export type SelectableValues =
  | (typeof ArraySelectableValues)[number]
  | (typeof ArraySelectableUnitsValues)[number]

export type RuleValue = SelectableValues | Separators | "free_text"

export const removeSpacesFromString = (string: string) => {
  return string.replaceAll(" ", "")
}

export type WritingLabelRulesOptions = {
  codesSentInUpperCase?: boolean
}

type FieldForRule = {
  max_length: number | null
  crop: CropType | null
  defaultCase: TransformType | null
  dateFormat: DateFormatType | null
  freeText: string
}

export const cropValue = (
  crop: CropType | null,
  value: string,
  maxLength?: number | null
) => {
  if (!value) return ""
  if (!crop) return value
  if (!maxLength) return value

  let croppedValue = ""

  switch (crop) {
    case "left":
      croppedValue = value.length > maxLength ? value.slice(-maxLength) : value
      break
    case "middle":
      if (value.length > maxLength) {
        const half = Math.floor(maxLength / 2)
        croppedValue = value.slice(0, half) + value.slice(-half)
      } else {
        croppedValue = value
      }
      break
    case "right":
      croppedValue =
        value.length > maxLength ? value.slice(0, maxLength) : value
      break
    default:
      throw new Error("crop not supported")
  }

  return croppedValue
}

export const transformValue = (
  transform: TransformType | null,
  value: string
) => {
  if (!value) return ""
  if (!transform) return value

  switch (transform) {
    case "lowercase":
      return value.toLowerCase()
    case "uppercase":
      return value.toUpperCase()
    default:
      throw new Error("transform not supported")
  }
}

export const formatDate = (
  dateFormat: DateFormatType | null,
  value: string,
  initialFormat: string = "yyyy-MM-dd"
) => {
  if (!value) return ""
  if (!dateFormat) return value

  const date = DateTime.fromFormat(value, initialFormat, {
    zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).toJSDate()

  switch (dateFormat) {
    case "dd_mm_yyyy":
      return DateTime.fromJSDate(date).toFormat("dd_MM_yyyy")
    case "yyyy_mm_dd":
      return DateTime.fromJSDate(date).toFormat("yyyy_MM_dd")
    default:
      throw new Error("date format not supported")
  }
}

export const formatAmount = (value: number) => {
  return value.toFixed(value % 1 === 0 ? 1 : 2)
}

const getLabelRuleValues = (
  fullDocument: FullDocumentOnly,
  writingLines: WritingLinePayload[],
  field: FieldForRule,
  values: RuleValue[],
  legalEntities: (LegalEntityInfo | LegalEntityWithoutInfos)[],
  options: WritingLabelRulesOptions,
  merchantCodesByRates: MerchantCodeByRate[] = []
) => {
  let valueField = ""

  values.forEach((value) => {
    switch (value) {
      case "amount_tax_included":
        const amountTaxIncludedWriting = writingLines.find((wl) =>
          wl.datum_type.includes("tax_included")
        )?.amount
        valueField += formatAmount(Number(amountTaxIncludedWriting || 0))
        break
      case "amount_tax_excluded":
        const amountTaxExcludedWriting = writingLines
          .filter((wl) => wl.datum_type.includes("tax_excluded"))
          .map((wl) => Number(wl.amount || 0))
          .reduce((acc, curr) => acc + curr, 0)
        valueField += formatAmount(amountTaxExcludedWriting)
        break
      case "including_tax_original_currency":
        const includingTaxOriginalCurrencyWriting = writingLines.find((wl) =>
          wl.datum_type.includes("tax_included")
        )?.currency_amount
        valueField += formatAmount(
          Number(includingTaxOriginalCurrencyWriting || 0)
        )
        break
      case "excluding_tax_original_currency":
        const excludingTaxOriginalCurrencyWriting = writingLines
          .filter((wl) => wl.datum_type.includes("tax_excluded"))
          .map((wl) => Number(wl.currency_amount || 0))
          .reduce((acc, curr) => acc + curr, 0)
        valueField += formatAmount(excludingTaxOriginalCurrencyWriting)
        break
      case "currency":
        valueField += writingLines[0]?.currency_code || ""
        break
      case "legal_entity_name":
        valueField += safeName(
          legalEntities.find(
            (le) => le.legal_entity_id === fullDocument.legal_entity_id
          )?.name || ""
        )
        break
      case "merchant_name":
        valueField += safeName(fullDocument.merchant_name)
        break
      case "merchant_code":
        const resolvedMerchantCodeByRate = resolveMerchantCodeByRate(
          merchantCodesByRates,
          fullDocument,
          writingLines
        )

        const merchantCode =
          resolvedMerchantCodeByRate?.merchantCodeByRate.merchant_code_value ||
          fullDocument.merchant_code

        valueField += options?.codesSentInUpperCase
          ? merchantCode.toUpperCase()
          : merchantCode.toLowerCase()
        break
      case "date":
        valueField += formatDate(
          field.dateFormat,
          fullDocument.fd_document_date,
          "dd/MM/yyyy"
        )
        break
      case "due_date":
        valueField += formatDate(
          field.dateFormat,
          fullDocument.fd_document_due_date || ""
        )
        break
      case "periodicity":
        valueField +=
          (fullDocument.periodicity_date_start
            ? formatDate(
                field.dateFormat,
                fullDocument.periodicity_date_start
              ) + ";"
            : "") +
          (fullDocument.periodicity_date_end
            ? formatDate(field.dateFormat, fullDocument.periodicity_date_end)
            : "")
        break
      case "document_reference":
        valueField += safeReference(fullDocument.fd_document_reference || "")
        break
      case "automatic_reference":
        valueField += safeReference(fullDocument.fd_automatic_reference || "")
        break
      case "document_reference_or_automatic_reference":
        valueField += safeReference(
          fullDocument.fd_document_reference ||
            fullDocument.fd_automatic_reference ||
            ""
        )
        break
      case "automatic_reference_or_document_reference":
        valueField += safeReference(
          fullDocument.fd_automatic_reference ||
            fullDocument.fd_document_reference ||
            ""
        )
        break
      case "original_batch_name":
        valueField += fullDocument.original_file_name
        break
      case "free_text_full_doc":
        valueField += fullDocument.free_text_full_doc || ""
        break
      case "free_text":
        valueField += field.freeText
        break
      case "space":
        valueField += " "
        break
      case "dot":
        valueField += "."
        break
      case "underscore":
        valueField += "_"
        break
      case "hyphen":
        valueField += "-"
        break
      default:
        valueField += ""
        break
    }
  })

  if (field.crop && field.max_length) {
    valueField = cropValue(field.crop, valueField, field.max_length)
  }

  if (field.defaultCase) {
    valueField = transformValue(field.defaultCase, valueField)
  }

  return valueField
}

/* eslint-disable camelcase */
export const simulateWritingLabelRule = (
  fullDocument: FullDocumentOnly | null,
  writingLines: WritingLinePayload[],
  field: Field,
  legalEntities: (LegalEntityInfo | LegalEntityWithoutInfos)[],
  options: WritingLabelRulesOptions,
  merchantCodesByRates: MerchantCodeByRate[] = []
) => {
  if (!fullDocument || !field) return ""

  try {
    let fieldRule: FieldForRule = {
      max_length: field.max_length || null,
      crop: field.crop,
      defaultCase: field.default_case,
      dateFormat: field.date_format,
      freeText: "",
    }
    let values: RuleValue[] = field.default_fields

    if (field.writing_label_rules.length > 0) {
      const writingLabelRules = field.writing_label_rules
        .filter(
          (rule) =>
            ["all", fullDocument.document_type].includes(rule.document_type) &&
            ["all", fullDocument.buy_or_sell].includes(rule.buy_or_sell)
        )
        .sort((a, b) => {
          if (a.document_type !== b.document_type) {
            return a.document_type.localeCompare(b.document_type) * -1 // DESC
          }
          if (a.buy_or_sell !== b.buy_or_sell) {
            return a.buy_or_sell.localeCompare(b.buy_or_sell) * -1 // DESC
          }

          return a.created_at > b.created_at ? 1 : -1 // ASC
        })

      if (writingLabelRules[0]) {
        const rule = writingLabelRules[0]

        fieldRule = {
          ...fieldRule,
          crop: rule.crop,
          defaultCase: rule.transform,
          dateFormat: rule.date_format,
          freeText: rule.free_text || "",
        }
        values = rule.values
      }
    }

    return getLabelRuleValues(
      fullDocument,
      writingLines,
      fieldRule,
      values,
      legalEntities,
      options,
      merchantCodesByRates
    )
  } catch (error) {
    return ""
  }
}
